import React, {useEffect, useMemo, useState} from 'react';
import {Spin} from "antd";
import useScreenSize from "../../../helpers/hooks/useScreenSize";
import styled from "styled-components";
import {
    Btn, ErrorText,
    FlexColumnSection,
    GreyTextClickable,
    Header,
    SeparatorVertical,
    Wrapper
} from "../../../styles/globalStyles";
import FormInput from "../../../components/UI/FormInput";
import { Formik } from 'formik';
import * as yup from "yup";
import {useDispatch, useSelector} from "react-redux";
import {login, otpLogin, resetError} from '@arboxappv4/shared/src/redux/authSlice'
import {
    FORGOT_PASSWORD_MODAL, ONE_TIME_PASSWORD_MODAL,
    openModal
} from "@arboxappv4/shared/src/redux/modalManagerSlice";
import {useNavigate} from "react-router-dom";
import {t} from "i18next";
import CustomModal from "../../../components/UI/CustomModal";
import {
    getLocationsForPurchase,
    isILPhone,
    isLocationIL,
    isValidIsraeliID,
    onLinkClicked
} from "../../../helpers/functions";
import Logo from "../../../components/Logo";
import {Divider, Button} from "antd";
import {Colors} from "@arboxappv4/shared/src/styles/Colors";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {apiAction} from "@arboxappv4/shared/src/helpers/HTTP";
import IconGenerator from "../../../helpers/IconGenerator";
import CustomPhone from "../../../components/UI/CustomPhone";
import { isValidPhoneNumber } from 'react-phone-number-input'


const Login = (props) => {
    const { isOpen, onClose, membership, email } = props;
    const error = useSelector(state => state.auth.error)
    const user = useSelector(state => state.user.data)
    const box = useSelector(state => state.site.box)
    const selectedLocation = useSelector(state => state.site.selectedLocation)
    const whitelabelProps = useSelector(state => state.site.whitelabelProps)
    const [authError, setAuthError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isPhoneOTPScreen, setIsPhoneOTPScreen] = useState(false)
    const [phoneOTPError, setPhoneOTPError] = useState(null);
    const {isMobile} = useScreenSize();
    const dispatch = useDispatch()
    const navigate = useNavigate();

    const allowPhoneVerify = useMemo(() => box?.boxes_settings?.some(setting => setting.prop_name === 'sitePhoneVerify' && setting.properties && setting.properties.active), [box])
    const hasPhoneOTP = useMemo(() => !!whitelabelProps?.properties?.loginOptions || allowPhoneVerify, [whitelabelProps, allowPhoneVerify]);
    const validationSchema = useMemo(() => {
        return yup.object().shape({
            email: yup.string().when([], {
                is: () => isPhoneOTPScreen,
                then: () => yup.string(),
                otherwise: () => yup.string().email(t('email-invalid-format')).required(t('email-required-field'))
            }),
            password: yup.string().when([], {
                is: () => isPhoneOTPScreen,
                then: () => yup.string(),
                otherwise: () => yup.string().required(t('mandatory-field'))
            }),
            phone: yup.string().when([], {
                is: () => isPhoneOTPScreen,
                then: () => yup.string().required(t('mandatory-field')).test('phone-validator', t('invalid-phone'), (value) => value && isValidPhoneNumber(value) === true),
                otherwise: () => yup.string()
            })
        })
    }, [isPhoneOTPScreen]);

    const locations = useMemo(() => getLocationsForPurchase(),[box])

    useEffect(() => {
        if(hasPhoneOTP) {
            setIsPhoneOTPScreen(true)
        }
    }, [hasPhoneOTP]);

    useEffect(() => {
        setAuthError(error ? t(error) : null)
        setLoading(false)
    }, [error]);

    useEffect(() => {
        if(user) { // if the user fetch was successful
            onClose()
        }
    }, [user]);

    const closeLogin = () => {
        dispatch(resetError(null))
        onClose()
    }

    const openForgotPassword = (email) => {
        closeLogin()
        dispatch(openModal({modalName: FORGOT_PASSWORD_MODAL, props: {email}}))
    }

    const goToRegister = () => {
        onLinkClicked('/register', navigate, {state: {membership: membership}})
        closeLogin()
    }

    const submit = async (values) => {
        setLoading(true)
        if(isPhoneOTPScreen) {
            submitPhoneOTP(values)
        } else {
            await dispatch(login(values))
        }

    }

    const submitPhoneOTP = async (values) => {
        try {
            if(!isILPhone(values.phone)) {
                throw {error: {messageToUser: 'globalPhoneError'}}
            }
            const res = await apiAction(`validatePhoneVerify`, 'post', {phone: values.phone});
            if(!res) throw {error: {messageToUser: 'phoneDoesntExistInBox'}}
            else if(res.isLeadInBox) throw {error: {messageToUser: 'leadPhoneInBoxError'}}
            const otpId = await apiAction(`mfa`, 'post', {type: 'sms', value: values.phone, check_format: true});
            dispatch(openModal({modalName: ONE_TIME_PASSWORD_MODAL, props: {type: 'sms', phone: values.phone, otpId: otpId}}))
            onClose()
        } catch (e) {
            setLoading(false)
            if(e?.error?.messageToUser === 'phoneDoesntExistInBox') {
                setPhoneOTPError(<span>
                        <ErrorText>{t('phoneDoesntExistInBox')} </ErrorText>
                        <GoToRegister style={{fontSize: '14px', color: Colors.siteBlack}} onClick={goToRegister}>{t('register-here')}</GoToRegister>
                    </span>)
            } else {
                setPhoneOTPError(t(e?.error?.messageToUser ?? 'phone-otp-error'))
            }
        }
    }

    return (
        <CustomModal open={isOpen} onCancel={closeLogin} width={isMobile ? 'inherit' : (locations.length > 0 ? '800px' : '500px')}>
            <Spin spinning={loading}>
            <Wrapper>
                <Formik initialValues={{ email: email ?? '', password: '', phone: '' }} validationSchema={validationSchema} onSubmit={submit}>
                    {({values, errors, touched, setFieldValue, handleSubmit}) => (
                        <FlexColumnSection gap={'20px'}>
                            {isMobile && <Logo onClick={closeLogin}/>}
                            <FlexColumnSection gap={'5px'} flex={'none'}>
                                <Header>{t('login')}</Header>
                                {isMobile ?
                                    <div>{t('dont-have-account')} <Clickable onClick={goToRegister}>{t('register-here')}</Clickable></div>
                                    :
                                    <div>{t('login-here')}</div>
                                }
                            </FlexColumnSection>
                            <FlexColumnSection gap={'20px'}>
                                {isPhoneOTPScreen ?
                                    <CustomPhone value={values.phone} error={(touched.phone && errors.phone) ? errors.phone : phoneOTPError} onChange={(val) => setFieldValue('phone', val)} country_code={selectedLocation?.country_code}/>
                                    :
                                    <FlexColumnSection gap={'5px'} flex={'none'}>
                                        <FlexColumnSection gap={'15px'}>
                                            <FormInput label={t('email')} value={values.email}
                                                       error={(touched.email && errors.email) ? errors.email : authError}
                                                       onChangeCallback={(val) => setFieldValue('email', val)}
                                                       type={'email'} autoComplete={'email'} inputMode={'email'}/>
                                            <FormInput label={t('password')} isPassword={true} value={values.password}
                                                       error={(touched.password && errors.password) ? errors.password : null}
                                                       onChangeCallback={(val) => setFieldValue('password', val)}/>
                                        </FlexColumnSection>
                                        <GreyTextClickable onClick={() => openForgotPassword(values.email)}>{t('forgot-password')}</GreyTextClickable>
                                    </FlexColumnSection>
                                }
                            </FlexColumnSection>
                            <Btn type={'primary'} onClick={handleSubmit} width={isMobile ? '100%' : null}>{t(isPhoneOTPScreen ? 'send-sms-opt' : 'login')}</Btn>
                            {hasPhoneOTP &&
                                <FlexColumnSection gap={'5px'} flex={'none'}>
                                    <Divider plain>{t('or-separator')}</Divider>
                                    <PhoneOTPBtn onClick={() => setIsPhoneOTPScreen(prev => !prev)}>
                                        <FontAwesomeIcon
                                            icon={IconGenerator({name: isPhoneOTPScreen ? 'envelope' : 'mobile-screen-button'})}
                                            color={Colors.siteBlack}/>
                                        {t(isPhoneOTPScreen ? 'email-login' : 'phone-otp-login')}
                                    </PhoneOTPBtn>
                                </FlexColumnSection>
                            }
                            <div>
                                <Conditions>{t('agree-to')} </Conditions>
                                <ConditionsLink onClick={() => window.open('https://www.arboxapp.com/terms-and-conditions', '_blank')}>{t('terms-of-Use')}</ConditionsLink>
                                <Conditions> {t('acknowledge-and-read')} </Conditions>
                                <ConditionsLink onClick={() => window.open('https://www.arboxapp.com/privacy-policy', '_blank')}>{t('privacy-policy')}</ConditionsLink>
                            </div>
                        </FlexColumnSection>
                    )}
                </Formik>
                {!isMobile && locations.length > 0 &&
                    <>
                        <SeparatorVertical margin={'0 40px'}/>
                        <FlexColumnSection gap={'20px'}>
                            <FlexColumnSection gap={'5px'} flex={'none'}>
                                <Header>{t('createAccount')}</Header>
                                <div>{t('register-subheader')}</div>
                            </FlexColumnSection>
                            <Btn type={'primary'} onClick={goToRegister}>{t('register')}</Btn>
                    </FlexColumnSection>
                </>
                }
            </Wrapper>
            </Spin>
        </CustomModal>
    );
};

export default Login;

const Conditions = styled.span`
    font-size: 12px;
`;

const ConditionsLink = styled(Conditions)`
    text-decoration: underline;
    cursor: pointer;
`;

const Clickable = styled.span`
    text-decoration: underline;
    cursor: pointer;
`;

const PhoneOTPBtn = styled(Button)`
    gap: 15px;
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    border-color: ${Colors.siteScheduleBorder};
`;
const SmallTextUnderline = styled.span`
    text-decoration: underline;
    font-size: ${({fontSize}) => fontSize ?? '16px'};
    font-weight: 400;
    cursor: pointer;
`;

const GoToRegister = styled(SmallTextUnderline)`
    font-size: 14px;
    color: ${Colors.siteBlack}
`